"use client"

import { useEffect, useRef } from "react"
import { toast } from "sonner"
import { useAuthStore } from "@/hooks/use-auth"

const DEFAULT_IDLE_MINUTES = 15

const SESSION_EXPIRED_MESSAGE: Record<"en" | "ar", string> = {
  en: "Your session has expired. Please login again.",
  ar: "انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.",
}

function getIdleTimeoutMs(): number {
  const raw = Number(
    process.env.NEXT_PUBLIC_SESSION_IDLE_TIMEOUT_MINUTES ?? DEFAULT_IDLE_MINUTES
  )
  const minutes = Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_IDLE_MINUTES
  return minutes * 60_000
}

/**
 * Client-side idle watchdog (UX only). Server still enforces idle/absolute
 * timeouts; this clears the local session when the user is inactive.
 */
export function useSessionIdleTimeout() {
  const tokens = useAuthStore((s) => s.tokens)
  const clearSession = useAuthStore((s) => s.clearSession)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const idleMs = getIdleTimeoutMs()

  useEffect(() => {
    if (!tokens?.access_token) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      return
    }

    const expireSession = () => {
      clearSession()
      const locale = window.location.pathname.startsWith("/ar") ? "ar" : "en"
      toast.error(SESSION_EXPIRED_MESSAGE[locale])
      window.location.href = `/${locale}/`
    }

    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(expireSession, idleMs)
    }

    const events: Array<keyof WindowEventMap> = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ]

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        resetTimer()
      }
    }

    resetTimer()
    for (const event of events) {
      window.addEventListener(event, resetTimer, { passive: true })
    }
    document.addEventListener("visibilitychange", onVisibility)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      for (const event of events) {
        window.removeEventListener(event, resetTimer)
      }
      document.removeEventListener("visibilitychange", onVisibility)
    }
  }, [tokens?.access_token, clearSession, idleMs])
}
